/* eslint-disable sort-keys */
import { billingPreferenceMap, maBillingPreferenceMap } from '../constants/common';
import { CRYPTO_TYPES, MA, MASTERCARD, VISA } from '../constants/partnering-networks';
import getCountryLocalePreference from './get-country-locale-preference';
import getMerchantCountryCode from './get-merchant-country-code';
import getShippingPreference from './get-shipping-preference';
import getTransactionAmountDetails from './get-transaction-amount-details';
import isNIRequestedCountry from './is-national-identifier-requested-country';
import isTrue from './is-true';
import OrchCustomData from './orch-custom-data';
import unescape, { unescapeMap } from './unescape';

/**
 * Checks vInitRequest for accepted countries and defaults to merchant config if none exist
 * @param {object} vInitRequest
 * @return {array}
 */
function getAcceptedBillingCountries(vInitRequest) {
  return vInitRequest?.payment?.billingCountries ?? [];
}

/**
 * @param {string} network
 * @return {boolean}
 */
function isMANetwork(network) {
  return network === MASTERCARD || network === MA;
}

/**
 * Checks vInitRequest for accepted countries and defaults to merchant config if none exist
 * @param {object} vInitRequest
 * @return {array}
 */
function getAcceptedShippingCountries(vInitRequest) {
  return vInitRequest?.shipping?.acceptedRegions;
}

/**
 * converts value
 * currently demo page does not have override value for collectBillingAddress,
 * only collectBillingAddress will be not available in vInitRequest
 * @param {object} merchantConfig
 * @param {string} network
 * @return {string}
 */
function getBillingPreference(merchantConfig, network) {
  const configValue = merchantConfig?.billingConstraints?.collectBillingAddress ?? 'FULL';
  const billingPreference = isMANetwork(network)
    ? maBillingPreferenceMap[configValue]
    : billingPreferenceMap[configValue];
  return billingPreference;
}

/**
 *
 * @param {object} vInitRequest
 * @param {object} merchantConfig
 * @param {string} network
 * @param {boolean} isTokenizationEnabled
 * @returns {boolean}
 */
function getEnableTokenization(vInitRequest, merchantConfig, network, isTokenizationEnabled) {
  let enableTokenization = vInitRequest?.tokenizationSetup?.enableTokenization ?? null;

  if (enableTokenization === null) {
    enableTokenization = merchantConfig?.tokenizationSetup?.enableTokenization ?? false;
  }

  if (isTokenizationEnabled && network !== VISA) {
    return false;
  }

  return isTrue(enableTokenization);
}

/**
 * Get dynamicDataType mapping for MA/Amex
 * @param {boolean} isTokenizationEnabled
 * @param {object} merchantConfig
 * @param {string} network
 * @param {object} vInitRequest
 * @returns {string}
 */
function getDynamicDataType(isTokenizationEnabled, merchantConfig, network, vInitRequest) {
  if (
    network === MA &&
    (!getEnableTokenization(vInitRequest, merchantConfig) || isTokenizationEnabled)
  ) {
    return null;
  }
  // As part of [VCO-13102 & VCO-13105] fix, added minor refactoring
  let cryptoType = null;
  let dynamicDataType = null;
  let tokenCryptogramTypes = [];
  //checks for tokenCryptogramType value is getting from vInit
  const tokenCryptogramType = vInitRequest?.tokenizationSetup?.tokenCryptogramType ?? null;

  if (tokenCryptogramType === null) {
    tokenCryptogramTypes = merchantConfig?.tokenizationSetup?.tokenCryptogramTypes ?? [];
    if (Array.isArray(tokenCryptogramTypes) && tokenCryptogramTypes.length) {
      cryptoType = tokenCryptogramTypes[0];
    }
  } else {
    cryptoType = tokenCryptogramType;
  }
  //decide dynamicDataType based on cryptoType
  const networkCryptoTypes = (network && CRYPTO_TYPES[network]) || null;
  dynamicDataType = (networkCryptoTypes && networkCryptoTypes[cryptoType]) || cryptoType;
  return dynamicDataType;
}

/**
 *
 * @param {object} vInitRequest
 * @param {object} merchantConfig
 * @returns {string}
 */
function getMerchantName(vInitRequest, merchantConfig) {
  let merchantName = vInitRequest.displayName || merchantConfig?.logo?.displayName;
  const unescapeMapKeys = Object.keys(unescapeMap);
  while (unescapeMapKeys.some(key => merchantName.includes(key))) {
    merchantName = unescape(merchantName);
  }
  return merchantName;
}
/**
 * Takes the configs we use for visa checkout and converts into the agreed upon
 * standard for INO
 * -- tokenCryptogramTypes.TAVV => dpaTransactionOptions.paymentOptions.dynamicDataType.CARDHOLDER_AUTHENTICATION_CRYPTOGRAM,
 * -- tokenCryptogramTypes.DTVV => dpaTransactionOptions.paymentOptions.dynamicDataType.CARD_SECURITY_CODE.
 * @param {object} configs
 * @param {string} network
 * @return {object} converted version of configs
 */
export default function(configs, network) {
  const {
    correlationId,
    isTokenizationEnabled,
    merchantConfig,
    vInitRequest //TODO check why object is getting in different format?
  } = configs;
  /* updated to address VCO-11255 */
  const srciDpaId = merchantConfig.clientId;
  const dynamicDataType = getDynamicDataType(
    isTokenizationEnabled,
    merchantConfig,
    network,
    vInitRequest
  );
  const dpaLocale = getCountryLocalePreference(configs);
  const merchantCountryCode = getMerchantCountryCode(vInitRequest, merchantConfig);
  /* set the default locale to orchcustomData which is obtained as part of config object */
  OrchCustomData.setDefaultDpaLocale(dpaLocale);
  // TODO: There are comments next properties that need to be investigated more. (null and TBD)
  return {
    srciTransactionId: correlationId,
    srciDpaId,
    srcInitiatorId: vInitRequest.apikey,
    dpaData: {
      srcDpaId: '', // No mapping from Backend
      dpaPresentationName: getMerchantName(vInitRequest, merchantConfig),
      dpaUri: vInitRequest.websiteUrl,
      dpaThreeDsPreference: vInitRequest?.threeDSSetup?.threeDSActive ? 'ONBEHALF' : 'UNKNOWN'
    },
    dpaTransactionOptions: {
      dpaLocale,
      dpaAcceptedBillingCountries: getAcceptedBillingCountries(vInitRequest),
      dpaAcceptedShippingCountries: getAcceptedShippingCountries(vInitRequest),
      dpaBillingPreference: getBillingPreference(merchantConfig, network),
      dpaShippingPreference: getShippingPreference(vInitRequest, network),
      consumerEmailAddressRequested: true,
      consumerNameRequested: true,
      consumerNationalIdentifierRequested: isNIRequestedCountry(merchantCountryCode),
      consumerPhoneNumberRequested: true,
      paymentOptions: {
        dpaDynamicDataTtlMinutes: vInitRequest?.dpaDynamicDataTtlMinutes ?? 2,
        dynamicDataType,
        dpaPanRequested: !getEnableTokenization(
          vInitRequest,
          merchantConfig,
          network,
          isTokenizationEnabled
        )
      },
      reviewAction: vInitRequest?.review?.buttonAction ?? 'CONTINUE',
      checkoutDescription: vInitRequest?.review?.message,
      transactionType: 'PURCHASE',
      orderType: null,
      payloadTypeIndicator: vInitRequest?.dataLevel ?? 'SUMMARY',
      ...getTransactionAmountDetails(vInitRequest),
      merchantOrderId: vInitRequest?.paymentRequest?.orderId,
      merchantCategoryCode: null,
      merchantCountryCode,
      threeDsInputData: {
        requestorId: '', // TBD with product
        acquirerId: '', // TBD with product
        acquirerMid: '' // TBD with product
      }
    }
  };
}
